/**
 * IdeasEmptyState.js
 * Placeholder shown when no ideas have been generated yet.
 */
import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import {
    AutoAwesome as AutoAwesomeIcon,
    Instagram as InstagramIcon,
    LinkedIn as LinkedInIcon,
    YouTube as YouTubeIcon,
    Twitter as TwitterIcon,
} from '@mui/icons-material';

const PLATFORM_PREVIEWS = [
    { icon: <InstagramIcon sx={{ fontSize: 18 }} />, label: 'Instagram Reel', color: '#e1306c' },
    { icon: <LinkedInIcon sx={{ fontSize: 18 }} />, label: 'LinkedIn Article', color: '#0077b5' },
    { icon: <YouTubeIcon sx={{ fontSize: 18 }} />, label: 'YouTube Short', color: '#ff0000' },
    { icon: <TwitterIcon sx={{ fontSize: 18 }} />, label: 'Twitter Thread', color: '#1da1f2' },
];

export default function IdeasEmptyState() {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{
            textAlign: 'center', p: 4, flex: 1,
            display: 'flex', flexDirection: 'column',
            justifyContent: 'center', alignItems: 'center',
            borderRadius: '12px',
            background: isDark
                ? `linear-gradient(135deg, ${alpha('#6366f1', 0.06)}, ${alpha('#8b5cf6', 0.04)})`
                : `linear-gradient(135deg, ${alpha('#6366f1', 0.03)}, ${alpha('#8b5cf6', 0.02)})`,
            border: `1.5px dashed ${alpha('#6366f1', 0.2)}`,
        }}>
            {/* Square icon box — not oval */}
            <Box sx={{
                width: 72, height: 72,
                borderRadius: '16px',
                background: `linear-gradient(135deg, ${alpha('#6366f1', 0.15)}, ${alpha('#8b5cf6', 0.15)})`,
                border: `1px solid ${alpha('#6366f1', 0.2)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 3,
            }}>
                <AutoAwesomeIcon sx={{ fontSize: 36, color: '#6366f1' }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 1.5, letterSpacing: '-0.02em' }}>
                AI-Powered Ideas Await
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 380, mx: 'auto', lineHeight: 1.7, mb: 4 }}>
                Enter a topic and select your platforms on the left to generate
                a high-converting content strategy tailored for your audience.
            </Typography>

            {/* Platform preview tiles */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                {PLATFORM_PREVIEWS.map(p => (
                    <Box key={p.label} sx={{
                        display: 'flex', alignItems: 'center', gap: 0.6,
                        px: 1.1, py: 0.5,
                        borderRadius: '6px',
                        bgcolor: alpha(p.color, 0.08),
                        border: `1px solid ${alpha(p.color, 0.2)}`,
                        color: p.color,
                    }}>
                        {React.cloneElement(p.icon, { sx: { fontSize: 14 } })}
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                            {p.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
